/**
 * TabWithBadge Component
 *
 * Tab label with an optional count badge.
 * Shows count of items in each tab for quick reference.
 * Enhanced with better visual hierarchy and animations.
 */

import PropTypes from "prop-types";
import { motion } from "framer-motion";
import Badge from "@mui/material/Badge";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

const badgeVariants = {
  initial: { scale: 0, opacity: 0 },
  animate: {
    scale: 1,
    opacity: 1,
    transition: { type: "spring", stiffness: 300, damping: 20 },
  },
  pulse: {
    scale: [1, 1.15, 1],
    transition: { duration: 0.4, ease: "easeInOut" },
  },
};

function TabWithBadge({ icon, label, count, color = "info", showBadge = true, isActive = false }) {
  const shouldShowBadge = showBadge && count > 0;
  const badgeColor = getBadgeColor(color);
  const badgeBgColor = getBadgeBgColor(color);

  return (
    <MDBox display="flex" alignItems="center" gap={0.75}>
      <Icon sx={{ fontSize: "1.25rem !important", opacity: isActive ? 1 : 0.7 }}>{icon}</Icon>
      <MDTypography
        component="span"
        variant="button"
        sx={{
          fontWeight: isActive ? 700 : 500,
          fontSize: "0.875rem",
          color: "inherit",
        }}
      >
        {label}
      </MDTypography>
      {shouldShowBadge && (
        <motion.div
          initial="initial"
          animate="animate"
          variants={badgeVariants}
          key={count} // Re-animate when count changes
        >
          <MDBox
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: count > 99 ? "28px" : "22px",
              height: "22px",
              borderRadius: "11px",
              backgroundColor: isActive ? badgeColor : badgeBgColor,
              color: isActive ? "white" : badgeColor,
              fontSize: "0.7rem",
              fontWeight: 700,
              px: 0.75,
              ml: 0.5,
              transition: "all 0.2s ease",
              border: isActive ? "none" : `1.5px solid ${badgeColor}`,
            }}
          >
            {count > 99 ? "99+" : count}
          </MDBox>
        </motion.div>
      )}
    </MDBox>
  );
}

function getBadgeColor(color) {
  const colors = {
    info: "#1A73E8",
    warning: "#FB8C00",
    success: "#4CAF50",
    error: "#F44335",
    secondary: "#7B809A",
  };
  return colors[color] || colors.info;
}

function getBadgeBgColor(color) {
  const colors = {
    info: "rgba(26, 115, 232, 0.08)",
    warning: "rgba(251, 140, 0, 0.08)",
    success: "rgba(76, 175, 80, 0.08)",
    error: "rgba(244, 67, 53, 0.08)",
    secondary: "rgba(123, 128, 154, 0.08)",
  };
  return colors[color] || colors.info;
}

TabWithBadge.propTypes = {
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  count: PropTypes.number,
  color: PropTypes.oneOf(["info", "warning", "success", "error", "secondary"]),
  showBadge: PropTypes.bool,
  isActive: PropTypes.bool,
};

export default TabWithBadge;
